import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PlusIcon,
  ChevronDownIcon,
  BeakerIcon,
  ShieldCheckIcon,
  CubeIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';

export interface HealthQuickActions {
  onAddHealthTest: () => void;
  onAddVaccination: () => void;
  onAddTreatment: () => void;
  onStartChecklist: () => void;
  onScheduleCheckup: () => void;
  onScheduleReminder: () => void;
  onStartAssessment: () => void;
  onInvestigateSymptoms?: () => void;
  onViewReports: () => void;
  onRunMonitoring?: () => void;
}

interface HealthQuickActionsMenuProps extends HealthQuickActions {
  showMonitoring?: boolean;
}

type ActionKey =
  | 'test'
  | 'vaccination'
  | 'treatment'
  | 'investigate'
  | 'checklist'
  | 'checkup'
  | 'reminder'
  | 'assessment'
  | 'reports'
  | 'monitoring';

const MENU_WIDTH = 240;
/** Above layout sticky (z-40) / sidebar (z-50) and page glass stacking contexts */
const MENU_Z_INDEX = 100;

const actionGroups: Array<{
  label: string;
  items: Array<{ key: ActionKey; label: string; icon: typeof BeakerIcon }>;
}> = [
  {
    label: 'Record',
    items: [
      { key: 'test', label: 'Health test', icon: BeakerIcon },
      { key: 'vaccination', label: 'Vaccination', icon: ShieldCheckIcon },
      { key: 'treatment', label: 'Treatment', icon: CubeIcon },
    ],
  },
  {
    label: 'Care',
    items: [
      { key: 'investigate', label: 'Investigate symptoms', icon: MagnifyingGlassIcon },
      { key: 'checklist', label: 'Health checklist', icon: ClipboardDocumentListIcon },
      { key: 'checkup', label: 'Vet checkup', icon: HeartIcon },
      { key: 'reminder', label: 'Reminder', icon: ClockIcon },
      { key: 'assessment', label: 'Health assessment', icon: DocumentChartBarIcon },
    ],
  },
  {
    label: 'Insights',
    items: [
      { key: 'reports', label: 'Generate report', icon: ChartBarIcon },
      { key: 'monitoring', label: 'Run monitoring scan', icon: ChartBarIcon },
    ],
  },
];

export default function HealthQuickActionsMenu({
  onAddHealthTest,
  onAddVaccination,
  onAddTreatment,
  onStartChecklist,
  onScheduleCheckup,
  onScheduleReminder,
  onStartAssessment,
  onInvestigateSymptoms,
  onViewReports,
  onRunMonitoring,
  showMonitoring = true,
}: HealthQuickActionsMenuProps) {
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, maxHeight: 448, openUpward: false });

  const handlers: Record<ActionKey, (() => void) | undefined> = {
    test: onAddHealthTest,
    vaccination: onAddVaccination,
    treatment: onAddTreatment,
    checklist: onStartChecklist,
    checkup: onScheduleCheckup,
    reminder: onScheduleReminder,
    assessment: onStartAssessment,
    investigate: onInvestigateSymptoms,
    reports: onViewReports,
    monitoring: onRunMonitoring,
  };

  const visibleGroups = useMemo(
    () =>
      actionGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (item.key === 'monitoring') return Boolean(showMonitoring && onRunMonitoring);
            if (item.key === 'investigate') return Boolean(onInvestigateSymptoms);
            return Boolean(handlers[item.key]);
          }),
        }))
        .filter((group) => group.items.length > 0),
    // Individual callbacks are the meaningful deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showMonitoring,
      onRunMonitoring,
      onInvestigateSymptoms,
      onAddHealthTest,
      onAddVaccination,
      onAddTreatment,
      onStartChecklist,
      onScheduleCheckup,
      onScheduleReminder,
      onStartAssessment,
      onViewReports,
    ],
  );

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const gutter = 8;
    const left = Math.min(
      Math.max(gutter, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - gutter,
    );
    const maxHeight = Math.min(448, window.innerHeight - 96);
    const spaceBelow = window.innerHeight - rect.bottom - gutter;
    const spaceAbove = rect.top - gutter;
    const openUpward = spaceBelow < Math.min(220, maxHeight) && spaceAbove > spaceBelow;
    const top = openUpward
      ? Math.max(gutter, rect.top - gutter)
      : rect.bottom + gutter;

    setCoords({ top, left, maxHeight, openUpward });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, updatePosition]);

  const runAction = (key: ActionKey) => {
    const handler = handlers[key];
    setOpen(false);
    handler?.();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-primary focus-pop text-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((prev) => !prev)}
      >
        <PlusIcon className="h-4 w-4" />
        Quick add
        <ChevronDownIcon className={`h-4 w-4 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Health quick add actions"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: MENU_WIDTH,
              maxHeight: coords.maxHeight,
              zIndex: MENU_Z_INDEX,
              transform: coords.openUpward ? 'translateY(-100%)' : undefined,
            }}
            className="overflow-y-auto rounded-xl border border-slate-200/80 bg-white py-1 shadow-xl ring-1 ring-black/5"
          >
            {visibleGroups.map((group) => (
              <div key={group.label} role="group" aria-label={group.label} className="py-1">
                <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    onClick={() => runAction(item.key)}
                    className="group flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50 focus:outline-none"
                  >
                    <item.icon className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-cyan-600" />
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
